// FILE: src/services/seoHealthService.js
//
// Admin SEO Health data (Content Factory):
//   • fetchGscMetrics  — Google Search Console summary via the gsc-metrics
//     edge function (admin-gated server-side; results cached ~6h, pass
//     { refresh: true } to force a live refetch).
//   • fetchContentSeoHealth — content-attributed telemetry aggregates via
//     the admin_content_seo_health RPC (migration 088).
//
// Both throw on failure; the view renders each section independently so a
// GSC outage never hides the telemetry side (and vice versa).

import { supabase } from './supabaseClient';

export async function fetchGscMetrics({ refresh = false } = {}) {
  const { data, error } = await supabase.functions.invoke('gsc-metrics', {
    body: { refresh },
  });
  if (error) throw new Error(`[seoHealthService] gsc-metrics: ${error.message}`);
  if (data && data.error) throw new Error(`[seoHealthService] gsc-metrics: ${data.error}`);
  return data;
}

/**
 * The Content Factory measurement model v2 (migration 092).
 *
 * NOT A LINEAR FUNNEL. The journey branches after app arrival into two
 * OVERLAPPING paths, because PM Pixel lets anonymous visitors answer
 * questions without registering:
 *
 *   shared acquisition   search exposure -> article view -> app arrival
 *   anonymous engagement app arrival -> first question -> 5Q activation
 *   account conversion   app arrival -> registration -> confirmation
 *                                    -> authenticated engagement
 *
 * Neither lower path is a prerequisite for the other. The superseded v1 chain
 * (...confirmation -> first question...) produced a 567% "rate" because it
 * divided across populations that do not nest. Do not reconstruct it — see
 * src/services/contentFunnelModel.js and
 * docs/content-factory-observation-report-spec.md.
 *
 * The payload segments by content_id, cert and grouped acquisition source
 * (including AI assistants), separates article_first from app_first arrivals,
 * and carries a traffic-quality classification with raw counts retained
 * alongside the canonical human-qualified counts.
 *
 * GSC impressions are stage 1 but are NOT in this payload — they come from
 * fetchGscMetrics and are joined by content_id -> canonical URL in the report
 * layer, so a Search Console outage degrades one stage instead of blanking
 * the whole funnel.
 */
export async function fetchContentFunnel(days = 30) {
  const { data, error } = await supabase.rpc('admin_content_funnel', { p_days: days });
  if (error) throw new Error(`[seoHealthService] admin_content_funnel: ${error.message}`);
  return data;
}

export async function fetchContentSeoHealth(days = 7) {
  const { data, error } = await supabase.rpc('admin_content_seo_health', { p_days: days });
  if (error) throw new Error(`[seoHealthService] admin_content_seo_health: ${error.message}`);
  return data;
}

/**
 * Pure: shape the two payloads into the signal-ladder summary tiles.
 *
 * TWO DIFFERENT SCOPES LIVE IN THE GSC PAYLOAD, and conflating them is the
 * single easiest way to misread this screen:
 *
 *   • `gsc.totals`  — SITE-WIDE. The edge function's searchAnalytics call uses
 *     the `page` dimension with no URL filter, so the totals cover every page
 *     on the property: homepage, app views, landing pages, articles, the lot.
 *   • `gsc.pages`   — ARTICLES ONLY. Built from the sitemap, filtered to
 *     `/articles/`. This is the Content Factory.
 *
 * A young property can therefore show healthy site-wide clicks (brand and
 * homepage searches) while the articles have earned none at all. Reporting the
 * site-wide number on a Content Factory screen makes the content look like it
 * is working when it has not started. Both are returned here, named for their
 * scope, so a caller has to choose deliberately.
 *
 * `firstOrganicClick` is keyed on ARTICLE clicks for the same reason: a
 * homepage click is not the Content Factory's first click.
 *
 * Per Search Console's own attribution, per-article impressions will not
 * reconcile against the site-wide totals. That is expected, not a bug — see
 * docs/content-factory-observation-report-spec.md §4.
 */
export function deriveSignalLadder(gsc, content) {
  const pages = gsc?.pages || [];
  const items = content?.content || [];
  const organic = items.reduce((a, c) => a + (c.organic_total || 0), 0);
  const registrations = items.reduce((a, c) => a + (c.registrations || 0), 0);
  const pageSum = (field) => pages.reduce((a, p) => a + (p[field] || 0), 0);
  const articleClicks28 = gsc ? pageSum('clicks28') : 0;

  return {
    indexed: gsc ? `${gsc.indexedCount ?? 0}/${pages.length}` : '—',

    // Content Factory articles only.
    articleImpressions7: gsc ? pageSum('impressions7') : null,
    articleImpressions28: gsc ? pageSum('impressions28') : null,
    articleClicks7: gsc ? pageSum('clicks7') : null,
    articleClicks28: gsc ? articleClicks28 : null,

    // Whole property, every page.
    siteImpressions7: gsc?.totals?.impressions7 ?? null,
    siteImpressions28: gsc?.totals?.impressions28 ?? null,
    siteClicks7: gsc?.totals?.clicks7 ?? null,
    siteClicks28: gsc?.totals?.clicks28 ?? null,

    organicArrivals: organic,
    attributedRegistrations: registrations,
    firstOrganicClick: articleClicks28 > 0,
  };
}
